import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Activity } from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import Logo from "../components/Logo";
import { ACTION_META, getActionMeta } from "../constants/actions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = ["all", "search", "save", "audit", "email", "manual_add"];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch { return iso; }
}

function detailSummary(action, details = {}) {
  if (action === "search") {
    return `${details.business_type || details.keyword || "todos"} · ${details.location || "—"} · ${details.result_count} resultados`;
  }
  if (action === "save" || action === "manual_add") {
    return `${details.name || "—"} · ${details.sector || "—"} · score ${details.score ?? "—"} ${details.badge ? `· ${details.badge}` : ""}${details.sheets_synced ? " · sheets ✓" : ""}`;
  }
  if (action === "audit") {
    return `${details.name || "—"} · ${details.sector || "—"} · score ${details.score ?? "—"}`;
  }
  if (action === "email") {
    return `${details.name || "—"} · "${details.subject || "—"}"`;
  }
  return JSON.stringify(details);
}

export default function Log() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/activity-log`, { params: { limit: 200 } });
      setItems(data.items || []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.action === filter);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="log-header"
          className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[#111827]">Activity Log</h1>
              <p className="text-[12.5px] text-[#6B7280] mt-0.5">Histórico de buscas, saves, audits e emails</p>
            </div>
          </div>
          <button
            data-testid="log-refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#E5E7EB] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </header>

        <main className="px-8 py-6 max-w-[1100px] mx-auto space-y-4">
          {/* Filter chips */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="log-filters">
            {FILTERS.map((f) => {
              const meta = ACTION_META[f];
              const active = filter === f;
              return (
                <button
                  key={f}
                  data-testid={`log-filter-${f}`}
                  onClick={() => setFilter(f)}
                  className={
                    "px-3 h-8 rounded-full text-[12px] font-medium transition-all border " +
                    (active
                      ? "bg-[#1A1A2E] text-white border-[#1A1A2E]"
                      : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#3B82F6] hover:text-[#3B82F6]")
                  }
                >
                  {f === "all" ? "Todos" : (meta?.label || f)}
                  {f !== "all" && (
                    <span className="ml-1.5 text-[10px] opacity-70">
                      {items.filter((i) => i.action === f).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-[#3B82F6]" />
            </div>
          ) : error ? (
            <ErrorState
              testid="log-error"
              title="Não foi possível carregar o log"
              message={error}
              onRetry={load}
            />
          ) : filtered.length === 0 ? (
            <div className="al-card p-10 text-center">
              <Activity className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
              <p className="text-[14px] font-semibold text-[#111827]">Nenhuma atividade registrada</p>
              <p className="text-[12.5px] text-[#6B7280] mt-1">
                Faça uma busca, salve um lead ou rode um audit para gerar entradas no log.
              </p>
            </div>
          ) : (
            <div className="al-card p-0 overflow-hidden" data-testid="log-list">
              {filtered.map((it, idx) => {
                const meta = getActionMeta(it.action);
                const Icon = meta.icon;
                return (
                  <div
                    key={it.id || idx}
                    data-testid={`log-item-${idx}`}
                    className="flex items-start gap-4 px-5 py-4 border-b border-[#F3F4F6] last:border-b-0 hover:bg-[#FAFAFB] transition-colors"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `${meta.color}1A`, color: meta.color }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13.5px] font-semibold text-[#111827]">{meta.label}</span>
                        <span className="text-[11px] text-[#9CA3AF]">·</span>
                        <span className="text-[12px] text-[#6B7280]">{fmtTime(it.timestamp)}</span>
                      </div>
                      <div className="text-[12.5px] text-[#4B5563] mt-1 leading-relaxed break-words">
                        {detailSummary(it.action, it.details)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
